// src/features/admin/components/layout/AdminLayout.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import {
  LayoutDashboard,
  Package,
  Receipt,
  Plus,
  BarChart3,
  Settings,
  X,
  Menu,
  LogOut,
  Eye,
  Tag
} from 'lucide-react';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: '/admin/dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />
    },
    {
      name: 'Products',
      href: '/admin/products',
      icon: <Package className="w-5 h-5" />
    },
    {
      name: 'Orders',
      href: '/admin/orders',
      icon: <Receipt className="w-5 h-5" />
    },
    {
      name: 'Categories',
      href: '/admin/categories',
      icon: <Tag className="w-5 h-5" />
    },
    {
      name: 'Add Product',
      href: '/admin/products/new',
      icon: <Plus className="w-5 h-5" />
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: <BarChart3 className="w-5 h-5" />
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: <Settings className="w-5 h-5" />
    }
  ];

  const isCurrentPage = (href) => {
    if (href === '/admin/dashboard') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="flex min-h-screen bg-brand-black text-brand-light">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-brand-black/80 backdrop-blur-lg z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Brand Dark with Gold Accents */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-brand-black border-r border-brand-red/20 shadow-[0_20px_60px_-30px_rgba(230,0,0,0.7)] transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative lg:flex lg:flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex items-center justify-between h-20 px-6 border-b border-brand-red/20 flex-shrink-0">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-brand-red rounded-lg flex items-center justify-center mr-3 shadow-sm">
              <span className="text-brand-light font-heading font-bold text-sm">PY</span>
            </div>
            <div>
              <span className="text-lg font-heading font-semibold text-brand-gold block">Admin Panel</span>
              <span className="text-xs text-brand-light/70 font-body font-light">Pontso Yarona</span>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 text-brand-light/60 hover:text-brand-gold hover:bg-brand-black/60 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 flex flex-col overflow-y-auto">
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  navigate(item.href);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-4 py-3 text-sm font-body font-medium rounded-lg transition-all duration-200 ${
                  isCurrentPage(item.href)
                    ? 'bg-brand-red/20 text-brand-gold border border-brand-red/40 shadow-[0_15px_45px_-30px_rgba(230,0,0,0.7)]'
                    : 'text-brand-light/70 hover:bg-brand-black/70 hover:text-brand-light border border-transparent'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.name}
              </button>
            ))}

            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t border-brand-red/20">
              <h3 className="text-xs font-body font-semibold text-brand-gold uppercase tracking-[0.3em] mb-3 px-4">
                Quick Actions
              </h3>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    navigate('/admin/products/new');
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-2.5 text-sm text-brand-black bg-brand-gold rounded-lg hover:bg-brand-gold/80 transition-colors font-body shadow-[0_20px_45px_-30px_rgba(255,195,0,0.6)]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </button>
                <button
                  onClick={() => {
                    window.open('/', '_blank');
                  }}
                  className="w-full flex items-center px-4 py-2.5 text-sm text-brand-light/70 bg-brand-black/70 rounded-lg hover:bg-brand-black/50 hover:text-brand-light transition-colors font-body border border-brand-red/20"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Site
                </button>
              </div>
            </div>
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-brand-red/20 flex-shrink-0">
            <div className="flex items-center mb-3">
              <div className="h-10 w-10 bg-brand-gold/15 border border-brand-gold/40 rounded-full flex items-center justify-center">
                <span className="text-brand-gold text-sm font-body font-medium">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-body font-medium text-brand-light truncate">
                  {user?.email?.split('@')[0]}
                </p>
                <p className="text-xs text-brand-light/60 font-body font-light truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-body font-medium text-brand-light bg-brand-red/20 hover:bg-brand-red/30 border border-brand-red/40 rounded-lg transition-all duration-200 hover:shadow-[0_10px_30px_-15px_rgba(230,0,0,0.5)]"
              title="Sign Out"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top navigation */}
        <div className="sticky top-0 z-30 bg-brand-black/90 backdrop-blur-lg shadow-md border-b border-brand-red/20 lg:hidden">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-brand-gold hover:text-brand-light hover:bg-brand-black/60 rounded-lg transition-colors touch-manipulation"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center">
              <div className="h-8 w-8 bg-brand-red rounded-lg flex items-center justify-center mr-2 shadow-sm">
                <span className="text-brand-light font-heading font-bold text-xs">PY</span>
              </div>
              <span className="text-lg font-heading font-semibold text-brand-gold">Admin</span>
            </div>
            <button
              onClick={handleSignOut}
              className="p-2 text-brand-light/60 hover:text-brand-gold hover:bg-brand-black/60 rounded-lg transition-colors touch-manipulation"
              title="Sign Out"
              aria-label="Sign Out"
            >
              <LogOut className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-brand-black">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
